import {
  UniversalContext,
  useUniversalContext,
} from "@cxc/react-universal-components";
import { useEffect, useState } from "react";
import {
  GetPageContentResult,
  getAuthenticatedPageContent,
  getUnauthenticatedPageContent,
} from "../../clients";
import { AemPageResponses, AemStruct } from "../../clients/AEMTypes";
import {
  RankedPersonaReturnProps,
  RankedPersonaValue,
} from "./useRankedPersonas";

interface UseFetchedPagesProps {
  pagesToFetch: string[];
  aemBaseUrl: string;
  rankedPersonaObject: RankedPersonaReturnProps;
  scope: string;
  locale: string;
  authenticated: boolean;
}

interface UseFetchedPagesReturnProps {
  pagesToProvide: AemPageResponses;
}

const isFulfilled = <T,>(
  input: PromiseSettledResult<T>,
): input is PromiseFulfilledResult<T> => input.status === "fulfilled";

const getAllPages = (
  locale: string,
  results: PromiseSettledResult<GetPageContentResult>[],
  setScopedValue: {
    (path1: string, path2: string, value: unknown): void;
    (scope: string, key: string, value: Partial<AemStruct>): void;
  },
  scope: string,
  setPages: (
    value:
      | ((prevState: AemPageResponses) => AemPageResponses)
      | AemPageResponses,
  ) => void,
  setPageLoadFailed: (
    value: ((prevState: boolean) => boolean) | boolean,
  ) => void,
  subscribeScopedValue: (scope: string, key: string) => Partial<AemStruct>,
) => {
  if (results.every(isFulfilled)) {
    const entries = results.map((result) => {
      const pageName = result.value.page + "|" + locale;
      const cachedAemJson = subscribeScopedValue(scope, pageName);

      if (cachedAemJson !== result.value.response) {
        setScopedValue(scope, pageName, result.value.response);
      }

      return [result.value.page, result.value.response];
    });
    setPages(Object.fromEntries(entries) as AemPageResponses);
  } else {
    setPageLoadFailed(true);
    console.error("Could not fetch one of the pages");
    return;
  }
};

const getAemPageContent = (
  subscribeScopedValue: (scope: string, key: string) => Partial<AemStruct>,
  scope: string,
  page: string,
  authenticated: boolean,
  aemBaseUrl: string,
  rankedPersonaValue: RankedPersonaValue,
  locale: string,
) => {
  const pageName = page + "|" + locale;
  const cachedAemJson = subscribeScopedValue(scope, pageName) as AemStruct;

  if (cachedAemJson) {
    return Promise.resolve({ page, response: cachedAemJson });
  } else {
    return authenticated
      ? getAuthenticatedPageContent({
          page,
          aemBaseUrl,
          rankedPersonasResponse: rankedPersonaValue,
          locale,
        })
      : getUnauthenticatedPageContent({
          page,
          aemBaseUrl,
          locale,
        });
  }
};

const useFetchedPages = ({
  pagesToFetch,
  aemBaseUrl,
  rankedPersonaObject,
  scope,
  locale,
  authenticated,
}: UseFetchedPagesProps): {
  pagesToProvide: AemPageResponses;
  pageLoadFailed: boolean;
} => {
  const { subscribeScopedValue, setScopedValue } =
    useUniversalContext() as UniversalContext;

  const { rankedPersonaValue } = rankedPersonaObject;

  const [pagesToProvide, setPages] = useState<AemPageResponses>({});
  const [pageLoadFailed, setPageLoadFailed] = useState(false);

  useEffect(() => {
    const fetchFromAEMorScopedContext = (
      page: string,
    ): Promise<GetPageContentResult> => {
      return getAemPageContent(
        subscribeScopedValue,
        scope,
        page,
        authenticated,
        aemBaseUrl,
        rankedPersonaValue,
        locale,
      );
    };

    const processFulfilledPromisesWithPageData = (
      results: PromiseSettledResult<GetPageContentResult>[],
    ) => {
      getAllPages(
        locale,
        results,
        setScopedValue,
        scope,
        setPages,
        setPageLoadFailed,
        subscribeScopedValue,
      );
    };

    if (pagesToFetch.length === 0) {
      console.error("pagesToFetch is empty");
      setPageLoadFailed(true);
      return;
    }

    if (aemBaseUrl === "") {
      console.error("aemBaseUrl is empty");
      setPageLoadFailed(true);
      return;
    }

    if (
      !authenticated ||
      (!!rankedPersonaValue && Object.keys(rankedPersonaValue).length > 0)
    ) {
      if (
        !!rankedPersonaValue &&
        Object.keys(rankedPersonaValue).includes("error")
      ) {
        setPageLoadFailed(true);
        return;
      }
      const promises = pagesToFetch.map(fetchFromAEMorScopedContext);
      if (promises && promises.length > 0) {
        Promise.allSettled(promises)
          .then(processFulfilledPromisesWithPageData)
          .catch(() => {
            console.error("Failed to fetch at least one page");
            setPageLoadFailed(true);
            return;
          });
      }
    }
  }, [
    aemBaseUrl,
    authenticated,
    locale,
    pagesToFetch,
    rankedPersonaValue,
    scope,
    setScopedValue,
    subscribeScopedValue,
  ]);

  return {
    pagesToProvide,
    pageLoadFailed,
  };
};

export { useFetchedPages };
export type { UseFetchedPagesProps, UseFetchedPagesReturnProps };
